import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import PageHero from "@/components/PageHero";
import { SCHOOL } from "@/data/content";

const SECTIONS = [
  {
    title: "Overview",
    body: [
      `Eletech Global Ontario School ("EGOS", "we", "us") is a private, ministry-inspected online high school serving students in Ontario, across Canada and around the world. This Privacy Policy explains what personal information we collect through our website and enrolment process, why we collect it, how we protect it, and the choices you have.`,
      `We follow the Personal Information Protection and Electronic Documents Act (PIPEDA) and, for student records, the applicable duties of an inspected Ontario private school under Ontario's Education Act and its regulations.`,
    ],
  },
  {
    title: "1 — Information we collect and why",
    body: [
      "Registration and enrolment information. When you apply or enrol, we collect the student's name, date of birth, contact details, grade level, the parent or guardian's contact details where the student is a minor, and any information you choose to share in your application message. We use it to process the application, communicate with you and set up the student's courses.",
      "Educational information. To deliver the Ontario Secondary School Diploma (OSSD) program we collect report cards, transcripts, identity documents and prior school records you submit, evidence of prior learning, course work and marks, final examinations and Ontario Student Record (OSR) information. We use it to place the student, assess credits, grant and report credits, and prepare the Ontario Student Transcript (OST).",
      "Website and communication information. When you use our forms, newsletter or demo page, we collect the details you submit (such as name, email, phone number and message). We also collect basic technical information such as IP address, browser type and pages visited, to keep the site secure and to understand how it is used.",
      "Payment and billing information. Where fees apply, we collect the billing details needed to issue invoices and receipts.",
    ],
  },
  {
    title: "2 — Consent",
    body: [
      "By submitting an application, enrolling a student or using our forms, you consent to the collection, use and disclosure of personal information for the purposes described in this policy. In some cases — for example, requesting an Ontario Student Record from a previous school — your written or implied consent is required to complete the enrolment.",
      "You may withdraw or adjust your consent at any time, subject to legal and educational record-keeping obligations, by contacting us using the details at the end of this policy. Withdrawing consent may limit our ability to deliver some services, such as issuing transcripts.",
    ],
  },
  {
    title: "3 — Student records",
    body: [
      "As an inspected private school in Ontario, we maintain an Ontario Student Record (OSR) for each student, containing enrolment information, academic results and other records required by Ontario regulation. The OSR is retained for the period required by Ontario education regulations and then destroyed or archived in accordance with them.",
      "The Ontario Student Transcript (OST) is an official record of secondary school credits. We release transcripts to students, parents or guardians, and to post-secondary organizations such as OUAC and OCAS when you ask us to send them.",
      "Parents and adult students may request access to the OSR, and may add or respond to information in it, by contacting us.",
    ],
  },
  {
    title: "4 — How your information is shared",
    body: [
      "We do not sell personal information. We share it only as needed to operate the school: with the Ontario Ministry of Education and inspectors as required by law; with post-secondary application centres such as OUAC and OCAS when you request transcript delivery; with service providers who host our systems, deliver our email or process payments, each bound to protect the information and use it only for our services; and where disclosure is required by law, legal process or to protect the safety of our students and staff.",
      "When you ask us to send records to another school, that school becomes responsible for the records it receives.",
    ],
  },
  {
    title: "5 — Cookies and similar technologies",
    body: [
      "Our website and learning platform use cookies and similar browser technologies that are necessary to keep you signed in, remember your preferences and keep the platform secure. We may also use analytics technologies that collect general, aggregated information about how visitors use the site — such as which pages are most visited — to improve it.",
      "You can block or delete cookies through your browser settings. Essential cookies are required for the learning platform to function, so restricting them may limit access to some features.",
    ],
  },
  {
    title: "6 — How we protect your information",
    body: [
      "We protect personal information with safeguards appropriate to its sensitivity, including encryption of data in transit, access limited to staff and service providers who need it to perform their duties, and administrative practices reviewed on a regular basis.",
      "If a security incident affects personal information in a way that creates a real risk of significant harm, we will notify affected individuals and keep a record of the incident, as required by law.",
    ],
  },
  {
    title: "7 — Retention",
    body: [
      "We keep personal information only as long as needed for the purposes described in this policy, including academic record-keeping required for an inspected Ontario private school, or as otherwise required by law. OSR and OST records are kept for the periods set by Ontario education regulations. Information collected through the website that does not lead to enrolment is retained for a reasonable period so we can respond to enquiries, and then deleted.",
    ],
  },
  {
    title: "8 — Your rights",
    body: [
      "You may ask us for access to the personal information we hold about you, ask for corrections, ask about the ways your information has been used, or withdraw consent as described above. Parents and guardians may exercise these rights on behalf of a minor student. To make a request, contact us using the details at the end of this policy; we will respond within a reasonable time.",
      "If you are not satisfied with our response, you may contact the Office of the Privacy Commissioner of Canada.",
    ],
  },
  {
    title: "9 — Children and students under 18",
    body: [
      "Our services are intended for high-school-aged students, and enrolment of a student under the age of majority requires the involvement and consent of a parent or guardian. We do not knowingly collect personal information from children through our website except as part of an application or enrolment made with a parent or guardian's involvement.",
    ],
  },
  {
    title: "10 — Links to other websites",
    body: [
      "Our website and learning platform may link to third-party websites or resources. We are not responsible for the privacy practices of those sites; we encourage you to read their privacy policies before providing personal information.",
    ],
  },
  {
    title: "11 — Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The current version is always posted on this page.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        lines={[<>Privacy <span className="font-serif italic font-medium text-brand">Policy</span></>]}
        desc="How Eletech Global Ontario School collects, uses, protects and shares personal information — written in plain language for students, parents and guardians."
        testid="privacy-page-heading"
      />
      <section className="container-x py-20 sm:py-24">
        <div className="max-w-3xl" data-testid="privacy-body">
          <Reveal>
            <p className="text-sm text-slate-500">Last updated: September 30, 2026</p>
          </Reveal>
          {SECTIONS.map((s) => (
            <Reveal key={s.title} delay={0.05}>
              <div className="mt-12 border-t border-slate-200 pt-10" data-testid={`privacy-section-${s.title.split(" ")[0].toLowerCase()}`}>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink">{s.title}</h2>
                <div className="mt-4 space-y-4">
                  {s.body.map((p, i) => (
                    <p key={i} className="text-[15px] leading-relaxed text-slate-600">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.05}>
            <div className="mt-14 rounded-3xl bg-ink p-8 text-ivory sm:p-10" data-testid="privacy-contact-card">
              <p className="eyebrow !text-brandlight">Questions about privacy?</p>
              <h2 className="mt-3 font-display text-2xl font-extrabold">Contact our privacy team</h2>
              <p className="mt-3 text-sm leading-relaxed text-ivory/70">
                Write to <strong className="text-ivory">{SCHOOL.email}</strong> and our team will respond within two business days.
              </p>
              <div className="mt-6 space-y-1 text-sm text-ivory/70">
                <p>{SCHOOL.name}</p>
                <p>{SCHOOL.address}</p>
                <p>{SCHOOL.phone}</p>
              </div>
              <Link to="/contact" className="btn-accent mt-8" data-testid="privacy-contact-link">Contact us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
